// ---------------------------------------------------------------------------
// Display Data Contracts — shared types for display-server and frontend
// ---------------------------------------------------------------------------

import { z } from "zod";

export interface DisplayQueueEntry {
  readonly position: number;
  readonly name: string;
  readonly service: string;
  readonly estimatedWait: number;
  readonly status: "waiting" | "in_progress" | "next";
}

export interface WaitingRoomReview {
  readonly id: string;
  readonly author: string;
  readonly quote: string;
  readonly rating: number;
  readonly publishedAt: string;
  readonly sourceType: string | null;
}

export interface WaitingRoomScheduleEntry {
  readonly id: string;
  readonly serviceName: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly enrolledCount: number;
  readonly maxParticipants: number | null;
  readonly availableSpots: number | null;
  readonly status: "upcoming" | "starting_soon" | "in_progress";
}

export interface WaitingRoomAnnouncement {
  readonly id: string;
  readonly title: string;
  readonly body: string;
}

export interface DisplayAppointment {
  readonly time: string;
  readonly name: string;
  readonly service: string;
  readonly checkedIn: boolean;
  readonly status: string;
}

export interface FrontDeskAppointment {
  readonly id: string;
  readonly time: string;
  readonly name: string;
  readonly service: string;
  readonly checkedIn: boolean;
  readonly status: string;
  readonly noShowRiskBadge: "LOW" | "MEDIUM" | "HIGH" | "VERY_HIGH" | null;
}

export interface FrontDeskAlert {
  readonly id: string;
  readonly kind: "late_arrival" | "high_no_show_risk" | "walk_in_queue";
  readonly severity: "info" | "warning" | "critical";
  readonly title: string;
  readonly detail: string;
  readonly bookingId: string | null;
}

export interface FrontDeskSummary {
  readonly totalToday: number;
  readonly completed: number;
  readonly cancelled: number;
  readonly noShow: number;
  readonly checkedIn: number;
  readonly walkInWaiting: number;
}

export interface DisplayStaffMember {
  readonly resourceId: string;
  readonly name: string;
  readonly bookings: number;
  readonly revenue: number;
  readonly tips: number;
  readonly rating: number;
  readonly utilization: number;
}

export interface DisplayBookingMatch {
  readonly bookingId: string;
  readonly name: string;
  readonly time: string;
  readonly service: string;
  readonly checkedIn: boolean;
}

export interface WaitingRoomData {
  readonly queue: readonly DisplayQueueEntry[];
  readonly reviews: readonly WaitingRoomReview[];
  readonly schedule: readonly WaitingRoomScheduleEntry[];
  readonly announcements: readonly WaitingRoomAnnouncement[];
  readonly lastUpdatedAt: string;
}

export interface WalkInQueueData {
  readonly entries: readonly DisplayQueueEntry[];
  readonly currentlyServing: string | null;
  readonly stats: {
    readonly totalWaiting: number;
    readonly avgWaitMinutes: number;
  };
  readonly lastUpdatedAt: string;
}

export interface WalkInQueueQrData {
  readonly joinToken: string;
  readonly expiresAt: string;
  readonly serviceId: string | null;
}

export interface WalkInQueueJoinResult {
  readonly ticketNumber: number;
  readonly position: number;
  readonly estimatedWait: number;
}

export interface WalkInQueueCallNextResult {
  readonly calledEntry: {
    readonly ticketNumber: number;
    readonly customerName: string;
  } | null;
  readonly queue: WalkInQueueData;
}

export interface FrontDeskData {
  readonly appointments: readonly FrontDeskAppointment[];
  readonly walkInQueue: readonly DisplayQueueEntry[];
  readonly alerts: readonly FrontDeskAlert[];
  readonly summary: FrontDeskSummary;
  readonly lastUpdatedAt: string;
}

export interface LeaderboardData {
  readonly staff: readonly DisplayStaffMember[];
  readonly period: "today" | "week" | "month";
  readonly lastUpdatedAt: string;
}

export interface LeaderboardResourceRow {
  readonly resourceId: string;
  readonly resourceName: string;
}

export interface LeaderboardBookingRow {
  readonly bookingId: string;
  readonly startAt: Date;
  readonly endAt: Date;
  readonly assignedResources: ReadonlyArray<{ resourceId: string; role?: string }> | null;
  readonly pricePaidCents: number | null;
  readonly tipAmount: string | number | null;
}

export interface LeaderboardRatingRow {
  readonly bookingId: string;
  readonly avgRating: string | number | null;
}

export interface LeaderboardScheduleRow {
  readonly resourceId: string;
  readonly dayOfWeek: number;
  readonly startTime: string;
  readonly endTime: string;
  readonly effectiveFrom?: Date | null;
  readonly effectiveTo?: Date | null;
}

export interface BuildLeaderboardDataInput {
  readonly resources: readonly LeaderboardResourceRow[];
  readonly bookings: readonly LeaderboardBookingRow[];
  readonly ratings: readonly LeaderboardRatingRow[];
  readonly schedules: readonly LeaderboardScheduleRow[];
  readonly period: LeaderboardData["period"];
  readonly now?: Date;
}

export interface KioskSearchResult {
  readonly matches: readonly DisplayBookingMatch[];
}

export interface KioskCheckInResult {
  readonly success: boolean;
  readonly name: string;
  readonly time: string;
  readonly service: string;
}

export type DisplayWsMessageType =
  | "initial_data"
  | "data_update"
  | "stale_warning"
  | "pong";

export interface DisplayWsMessage {
  readonly type: DisplayWsMessageType;
  readonly displayType: string;
  readonly data: unknown;
  readonly timestamp: string;
}

export const joinQueueSchema = z.object({
  customerName: z.string().min(1).max(100).optional(),
  serviceId: z.string().uuid().optional(),
});

export const kioskSearchSchema = z.object({
  q: z.string().min(1).max(200),
});

export const internalNotifySchema = z.object({
  tenantId: z.string().uuid(),
  event: z.enum([
    "booking_status_changed",
    "queue_updated",
    "check_in",
    "leaderboard_refresh",
  ]),
});

export function sanitizeDisplayName(fullName: string | null | undefined): string {
  if (!fullName) return "Guest";
  const parts = fullName.trim().split(/\s+/).filter((part) => part.length > 0);
  const [firstPart, ...rest] = parts;
  if (!firstPart) return "Guest";
  if (rest.length === 0) return firstPart;
  const lastPart = rest[rest.length - 1] ?? "";
  return lastPart.length > 0 ? `${firstPart} ${lastPart.charAt(0)}.` : firstPart;
}

export function getLeaderboardPeriodStart(
  period: LeaderboardData["period"],
  now: Date,
): Date {
  if (period === "today") {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  }

  if (period === "week") {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    start.setUTCDate(start.getUTCDate() - start.getUTCDay());
    return start;
  }

  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

function parseTimeToMinutes(value: string): number {
  const [hoursRaw = "0", minutesRaw = "0"] = value.split(":");
  const hours = Number.parseInt(hoursRaw, 10);
  const minutes = Number.parseInt(minutesRaw, 10);
  return (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);
}

function coerceTipAmountToCents(value: string | number | null): number {
  if (value == null) return 0;
  const numeric = typeof value === "number" ? value : Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric * 100);
}

function coerceRating(value: string | number | null): number | null {
  if (value == null) return null;
  const numeric = typeof value === "number" ? value : Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric;
}

function calculateScheduledMinutes(
  schedules: readonly LeaderboardScheduleRow[],
  periodStart: Date,
  now: Date,
): number {
  if (schedules.length === 0) return 0;

  let totalMinutes = 0;
  const cursor = new Date(Date.UTC(periodStart.getUTCFullYear(), periodStart.getUTCMonth(), periodStart.getUTCDate()));
  const finalDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  while (cursor <= finalDay) {
    for (const schedule of schedules) {
      if (schedule.dayOfWeek !== cursor.getUTCDay()) {
        continue;
      }

      if (schedule.effectiveFrom && cursor < schedule.effectiveFrom) {
        continue;
      }

      if (schedule.effectiveTo && cursor > schedule.effectiveTo) {
        continue;
      }

      totalMinutes += Math.max(0, parseTimeToMinutes(schedule.endTime) - parseTimeToMinutes(schedule.startTime));
    }

    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return totalMinutes;
}

export function buildLeaderboardData(input: BuildLeaderboardDataInput): LeaderboardData {
  const now = input.now ?? new Date();
  const periodStart = getLeaderboardPeriodStart(input.period, now);
  const ratingsByBookingId = new Map<string, number>();
  const schedulesByResourceId = new Map<string, LeaderboardScheduleRow[]>();

  for (const rating of input.ratings) {
    const numeric = coerceRating(rating.avgRating);
    if (numeric != null) {
      ratingsByBookingId.set(rating.bookingId, numeric);
    }
  }

  for (const schedule of input.schedules) {
    const existing = schedulesByResourceId.get(schedule.resourceId) ?? [];
    existing.push(schedule);
    schedulesByResourceId.set(schedule.resourceId, existing);
  }

  const aggregates = new Map<string, {
    bookings: number;
    revenue: number;
    tips: number;
    bookedMinutes: number;
    ratingTotal: number;
    ratingCount: number;
  }>();

  for (const booking of input.bookings) {
    if (booking.startAt < periodStart || booking.startAt > now) {
      continue;
    }

    const assignedResources = booking.assignedResources ?? [];
    if (assignedResources.length === 0) {
      continue;
    }

    const durationMinutes = Math.max(0, Math.round((booking.endAt.getTime() - booking.startAt.getTime()) / 60000));
    const rating = ratingsByBookingId.get(booking.bookingId);

    for (const assignment of assignedResources) {
      const resourceId = assignment.resourceId;
      const current = aggregates.get(resourceId) ?? {
        bookings: 0,
        revenue: 0,
        tips: 0,
        bookedMinutes: 0,
        ratingTotal: 0,
        ratingCount: 0,
      };

      current.bookings += 1;
      current.revenue += booking.pricePaidCents ?? 0;
      current.tips += coerceTipAmountToCents(booking.tipAmount);
      current.bookedMinutes += durationMinutes;
      if (rating != null) {
        current.ratingTotal += rating;
        current.ratingCount += 1;
      }

      aggregates.set(resourceId, current);
    }
  }

  const staff = input.resources.map((resource) => {
    const aggregate = aggregates.get(resource.resourceId) ?? {
      bookings: 0,
      revenue: 0,
      tips: 0,
      bookedMinutes: 0,
      ratingTotal: 0,
      ratingCount: 0,
    };
    const scheduledMinutes = calculateScheduledMinutes(
      schedulesByResourceId.get(resource.resourceId) ?? [],
      periodStart,
      now,
    );
    const utilization = scheduledMinutes > 0
      ? Math.min(100, Math.round((aggregate.bookedMinutes / scheduledMinutes) * 100))
      : 0;
    const rating = aggregate.ratingCount > 0
      ? Number((aggregate.ratingTotal / aggregate.ratingCount).toFixed(1))
      : 0;

    return {
      resourceId: resource.resourceId,
      name: sanitizeDisplayName(resource.resourceName),
      bookings: aggregate.bookings,
      revenue: aggregate.revenue,
      tips: aggregate.tips,
      rating,
      utilization,
    } satisfies DisplayStaffMember;
  });

  staff.sort((left, right) => (
    right.revenue - left.revenue
    || right.bookings - left.bookings
    || right.rating - left.rating
    || right.tips - left.tips
    || left.name.localeCompare(right.name)
  ));

  return {
    staff,
    period: input.period,
    lastUpdatedAt: now.toISOString(),
  };
}
